import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MASKED_SECRET, deriveApSsid } from '@glyph/protocol-ts';
import { fmtBytes, fmtDur, useApi, useCan, useCommand, useLogs, useSaveSettings, useStatus, useSystemInfo } from '@glyph/core';
import { Button, Card, CardHeader, Dialog, FactRow, Field, ListRow, Note, PageHeader, SecretInput, Section, SegmentedControl, Select, SwitchRow, TextInput, useToast } from '@glyph/ui';
import { DeviceUpdatePanel } from './UpdatesScreen';
import { fmtTs } from './shared';

type Tab = 'updates' | 'logs';

export interface SystemScreenProps {
  deviceId?: string;
  /** The unit's own shell: accounts (admin / tech passwords) are editable; the cloud never patches `auth`. */
  local?: boolean;
}

/** System page, the same in both shells: Software (updates, this unit, power), unit settings, Deepgram key, logs. */
export function SystemScreen({ deviceId, local = false }: SystemScreenProps) {
  const [tab, setTab] = useState<Tab>('updates');
  const status = useStatus(deviceId);
  const info = useSystemInfo(deviceId);
  const s = useSaveSettings(deviceId);
  const cmd = useCommand(deviceId);
  const toast = useToast();
  const logs = useLogs(deviceId);
  const [power, setPower] = useState<null | 'reboot' | 'shutdown'>(null);
  const st = status.data;
  const stats = st?.stats;
  const d = s.draft;
  const api = useApi();
  // What this account may touch here (the cloud enforces the same): the Deepgram key is admins and owners only;
  // the unit settings card (name, updates, system) is device admin; power follows reboot / shutdown.
  const canKey = useCan('credentials.deepgram.set');
  const canUnitSettings = useCan('setup.complete'); // capability settings.admin (device admin: admin, owner, event admin)
  const canReboot = useCan('system.reboot');
  const canShutdown = useCan('system.shutdown');
  const secrets = useQuery({ queryKey: ['settings-secrets'], queryFn: () => api.secrets('self'), retry: false, enabled: local });
  const shown = (value: string, revealed: string | undefined) => (value === MASKED_SECRET ? (revealed ?? '') : value);
  const [dgKey, setDgKey] = useState('');
  const [dgBusy, setDgBusy] = useState(false);
  const saveDgKey = async () => {
    setDgBusy(true);
    try {
      await cmd.run('credentials.deepgram.set', { api_key: dgKey.trim() });
      setDgKey('');
      toast.push('Deepgram key saved on the unit', 'ok');
      void status.refetch();
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    } finally {
      setDgBusy(false);
    }
  };
  const sections = local ? (['device', 'update', 'auth', 'system'] as const) : (['device', 'update', 'system'] as const);
  const localDirty = sections.some((k) => s.isDirty(k));
  const saveLocal = async () => {
    const a = s.draft?.auth;
    if (local && a) {
      if (secrets.data?.auth?.admin_password !== undefined && a.admin_password === secrets.data.auth.admin_password) s.update('auth', { admin_password: MASKED_SECRET });
      if (secrets.data?.auth?.tech_password !== undefined && a.tech_password === secrets.data.auth.tech_password) s.update('auth', { tech_password: MASKED_SECRET });
    }
    await s.save([...sections]);
    if (local) void secrets.refetch();
    toast.push('Settings saved', 'ok');
  };
  const tabs = [
    { value: 'updates', label: 'Updates' },
    { value: 'logs', label: 'Logs' },
  ] as const;
  return (
    <>
      <Section>
        <PageHeader desktopOnly title="System" meta={`${info.data?.model ?? 'Raspberry Pi 5'} · ${info.data?.os ?? ''} · agent ${st?.version?.agent ?? '—'} · slot ${st?.version?.slot ?? '—'}`} actions={<SegmentedControl value={tab} onChange={setTab} options={[...tabs]} />} />
        <div className="md:hidden">
          <SegmentedControl fullWidth value={tab} onChange={setTab} options={[...tabs]} />
        </div>
        {tab === 'updates' && (
          <div className="grid items-start gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <DeviceUpdatePanel deviceId={deviceId} />
            <Card>
              <CardHeader title="This unit" meta={info.data?.serial ?? ''} />
              <FactRow label="Model" value={info.data?.model ?? '—'} />
              <FactRow label="OS" value={`${info.data?.os ?? '—'} · ${info.data?.kernel ?? ''}`} />
              <FactRow label="Uptime" value={stats ? fmtDur(stats.uptime_s) : '—'} />
              <FactRow label="Storage" value={info.data?.disk ? `${fmtBytes(info.data.disk.free)} free of ${fmtBytes(info.data.disk.total)}` : stats ? `${stats.disk_free_gb} GB free` : '—'} valueTone={st?.storage?.ungrown ? 'warn' : undefined} />
              {st?.storage?.ungrown && (
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2.5">
                  <span className="text-base text-fg2">The data volume never grew to the card. Growing it takes a few seconds and needs no restart.</span>
                  <Button
                    size="responsive"
                    variant="primary"
                    disabled={!!cmd.pending['system.data.grow']}
                    onClick={async () => {
                      const r = await cmd.run('system.data.grow', {});
                      if (r) {
                        toast.push(`Data volume grown${r.free_gb != null ? ` · ${r.free_gb.toFixed(1)} GB free` : ''}`, 'ok');
                        void status.refetch();
                      } else if (cmd.lastError) toast.push(cmd.lastError.message, 'bad');
                    }}
                  >
                    Grow data volume
                  </Button>
                </div>
              )}
              <FactRow label="Memory" value={stats ? `${stats.mem_pct} % used` : '—'} />
              <FactRow label="Power" value={stats?.under_voltage ? 'UNDER-VOLTAGE' : stats?.throttled ? 'THROTTLED' : 'OK'} meta valueTone={stats?.under_voltage || stats?.throttled ? 'warn' : 'ok'} />
              <FactRow label="Temperature" value={stats?.temp_c != null ? `${stats.temp_c} °C` : '—'} />
              <div className="flex flex-wrap gap-2 border-t border-line px-4 py-3.5">
                <Button size="responsive" onClick={() => void cmd.run('system.identify', {})}>
                  Identify
                </Button>
                <Button
                  size="responsive"
                  onClick={async () => {
                    const r = await cmd.run('system.selftest', {});
                    if (r) toast.push(r.passed ? 'Self-test passed' : 'Self-test failed', r.passed ? 'ok' : 'bad');
                  }}
                >
                  Self-test
                </Button>
                {canReboot && (
                  <Button size="responsive" onClick={() => setPower('reboot')}>
                    Reboot
                  </Button>
                )}
                {canShutdown && (
                <Button size="responsive" variant="destructive-outline" onClick={() => setPower('shutdown')}>
                  Shut down
                </Button>
                )}
              </div>
            </Card>
            {d && (local || canUnitSettings) && (
              <Card mode="padded">
                <CardHeader title={local ? 'Local settings' : 'Unit settings'} meta={localDirty ? 'UNSAVED' : undefined} className="p-0 md:py-0" />
                <Field label="Device name" right={<span className="font-mono text-xs text-fg4">hotspot {deriveApSsid(d.device.name, d.network.ap_ssid_suffix)}</span>}>
                  <TextInput block value={d.device.name} onChange={(e) => s.update('device', { name: e.target.value })} />
                </Field>
                <Field label="Update channel">
                  <Select
                    block
                    value={d.update.channel}
                    onChange={(v) => s.update('update', { channel: v })}
                    options={[
                      { value: 'stable', label: 'Stable' },
                      { value: 'beta', label: 'Beta' },
                      { value: 'canary', label: 'Canary' },
                    ]}
                  />
                </Field>
                <SwitchRow first title="OLED status display" hint="128×32 · boot splash, IP page, stats page" checked={d.system.oled_enabled} onChange={(v) => s.update('system', { oled_enabled: v })} className="border-t-0 py-0" />
                <SwitchRow first title="Show hotspot password on OLED" hint="on the setup page while no client is connected" checked={d.system.oled_show_ap_pass} onChange={(v) => s.update('system', { oled_show_ap_pass: v })} className="border-t-0 py-0" />
                <Field label="OLED page when cloud-linked">
                  <SegmentedControl
                    value={d.system.oled_cloud_mode}
                    onChange={(v) => s.update('system', { oled_cloud_mode: v })}
                    options={[
                      { value: 'auto', label: 'Auto' },
                      { value: 'classic', label: 'Classic' },
                      { value: 'stats', label: 'Stats' },
                    ]}
                  />
                </Field>
                {local && (
                  <>
                    <Field label="FDG Admin password" right={<span className="font-mono text-xs text-fg4">username fdgadmin</span>}>
                      <SecretInput variant="mono" block autoComplete="new-password" value={shown(d.auth.admin_password, secrets.data?.auth?.admin_password)} onChange={(e) => s.update('auth', { admin_password: e.target.value })} />
                    </Field>
                    <Field label="Tech username">
                      <TextInput block autoCapitalize="none" value={d.auth.tech_username} onChange={(e) => s.update('auth', { tech_username: e.target.value })} />
                    </Field>
                    <Field label="Tech password">
                      <SecretInput variant="mono" block autoComplete="new-password" value={shown(d.auth.tech_password, secrets.data?.auth?.tech_password)} onChange={(e) => s.update('auth', { tech_password: e.target.value })} />
                    </Field>
                  </>
                )}
                <div className="flex gap-2">
                  <Button variant="primary" size="responsive" disabled={!localDirty || s.isSaving} onClick={() => void saveLocal()}>
                    Save
                  </Button>
                  {localDirty && (
                    <Button size="responsive" onClick={() => sections.forEach((k) => s.revert(k))}>
                      Revert
                    </Button>
                  )}
                </div>
              </Card>
            )}
            <Card mode="padded">
              <CardHeader title="Deepgram" meta={st?.credentials?.has_key ? `KEY ${String(st.credentials.active_key_id ?? '').toUpperCase()}` : 'NO KEY'} metaTone={st?.credentials?.has_key ? 'ok' : 'warn'} className="p-0 md:py-0" />
              {local || canKey ? (
                <>
                  <Field label="API key">
                    <SecretInput variant="mono" block autoComplete="off" value={dgKey} onChange={(e) => setDgKey(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <Button variant="primary" size="responsive" disabled={dgKey.trim().length < 20 || dgBusy} onClick={() => void saveDgKey()}>
                      Save key
                    </Button>
                  </div>
                  {!local && <Note>Sets the key on this unit only. The Devices page sends one key to all or selected units.</Note>}
                </>
              ) : (
                <Note>The Deepgram key is managed by your admins. Model, language and formatting are on the Speech page.</Note>
              )}
            </Card>
          </div>
        )}
        {tab === 'logs' && (
          <Card>
            <CardHeader title="Agent log" meta={`${logs.data?.length ?? 0} LINES · LIVE`} />
            {(logs.data ?? []).map((l, i) => (
              <ListRow key={i} className="md:min-h-0 md:py-2">
                <span className="font-mono text-sm leading-[1.5] text-fg4 tabular">{fmtTs(l.ts)}</span>
                <span className={`meta w-16 flex-none ${l.level === 'ERROR' ? 'text-bad' : l.level === 'WARNING' ? 'text-warn' : 'text-fg4'}`}>{l.level}</span>
                <span className="meta w-14 flex-none text-fg3">{l.src}</span>
                <span className="min-w-0 flex-1 font-mono text-sm leading-[1.5] text-fg2">{l.msg}</span>
              </ListRow>
            ))}
            <Note className="block px-4 py-3">In-memory ring of the last 2,000 lines (logs.tail) · full journal via `journalctl -u glyph-agent` on the maintenance console.</Note>
          </Card>
        )}
      </Section>
      <Dialog
        open={!!power}
        onClose={() => setPower(null)}
        title={power === 'reboot' ? 'Reboot this unit?' : 'Shut down this unit?'}
        meta={power === 'reboot' ? 'CAPTIONS DROP FOR ~30 S · HDMI RE-SYNCS' : 'OLED TURNS OFF · PULL POWER AFTER THE PANEL IS DARK'}
        confirm={{
          label: power === 'reboot' ? 'Reboot' : 'Shut down',
          destructive: true,
          onClick: async () => {
            await cmd.run(power === 'reboot' ? 'system.reboot' : 'system.shutdown', power === 'reboot' ? { confirm: true } : {});
            setPower(null);
          },
        }}
      >
        {power === 'reboot' ? 'The renderer boots straight to the boot colour and the session resumes.' : 'A clean shutdown closes the recording session and unmounts the data volume.'}
      </Dialog>
    </>
  );
}
